// The match highlight of the stock movement dialog's search (spec 054, research
// R12). A port of the wireframe's `evidentiaza`
// (wireframes/react/src/lib/cautare.ts), which the caiet glossary's "căutare
// extinsă" defines: capitals and diacritics are ignored, and a term matches at
// the start of a word. The segments come back in the original spelling, so the
// caller wraps the matched ones in <mark> without losing an accent.

export interface HighlightSegment {
  text: string;
  match: boolean;
}

const COMBINING_MARKS = /[̀-ͯ]/g;

export function normalizeForSearch(value: string): string {
  return value.toLowerCase().normalize('NFD').replace(COMBINING_MARKS, '');
}

/** The words of a query, normalized; anything that is not a letter or digit separates them. */
export function searchTerms(query: string): string[] {
  return normalizeForSearch(query)
    .split(/[^a-z0-9]+/)
    .filter((term) => term.length > 0);
}

// Normalized one character at a time, so index i of the result is index i of
// the text: the marks computed on the normalized form land on the original
// characters. A character whose folded form changes length is kept as it is
// rather than shifting every index after it; it then simply never matches.
function foldAligned(text: string): string {
  let out = '';
  for (const ch of text) {
    const folded = normalizeForSearch(ch);
    out += folded.length === ch.length ? folded : ch;
  }
  return out;
}

export interface HighlightOptions {
  /**
   * Mark only the first word-start match of each term (spec 054 T106): a
   * result row points the eye at one place, not at every "ca" in it.
   */
  firstOnly?: boolean | undefined;
}

export function highlightMatch(
  text: string,
  terms: readonly string[],
  options: HighlightOptions = {},
): HighlightSegment[] {
  const folded = foldAligned(text);
  const marked = new Array<boolean>(text.length).fill(false);
  for (const term of terms.map(normalizeForSearch).filter((t) => t.length > 0)) {
    let from = 0;
    while (from <= folded.length - term.length) {
      const at = folded.indexOf(term, from);
      if (at < 0) break;
      const wordStart = at === 0 || /[^a-z0-9]/.test(folded[at - 1] ?? '');
      if (wordStart) {
        marked.fill(true, at, at + term.length);
        if (options.firstOnly) break;
      }
      from = at + 1;
    }
  }

  const segments: HighlightSegment[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const match = marked[i] ?? false;
    const last = segments[segments.length - 1];
    if (last && last.match === match) last.text += text[i];
    else segments.push({ text: text[i] ?? '', match });
  }
  return segments;
}
